import { useEffect } from "react"
import {
    subscribeToMessages,
    checkTokenRefresh,
} from "../components/Notifications/firebase"
import { themedToast } from "../utils/themedToast"
import { useQueryClient } from "@tanstack/react-query"
import { useAuth } from "../contexts/AuthContext"
import { saveDeviceToken } from "../components/Notifications/notificationService"

const TOKEN_REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000 // 24 hours

export default function useForegroundMessages() {
    const queryClient = useQueryClient()
    const { auth } = useAuth()

    // Periodically validate the FCM token and sync any rotation to the server
    useEffect(() => {
        if (!auth?.token) return

        async function refresh() {
            try {
                const newToken = await checkTokenRefresh()
                if (newToken) {
                    await saveDeviceToken({
                        token: newToken,
                        accessToken: auth.token,
                    })
                }
            } catch (error) {
                console.error("Failed to refresh device token:", error)
            }
        }

        refresh() // check immediately on mount / login
        const interval = setInterval(refresh, TOKEN_REFRESH_INTERVAL_MS)
        return () => clearInterval(interval)
    }, [auth?.token])

    useEffect(() => {
        const unsubscribe = subscribeToMessages((payload) => {
            // Silent sync push — just refresh the list, no toast
            if (payload?.data?.type === "NOTIFICATION_SYNC") {
                queryClient.invalidateQueries(["getNotifications"])
                return
            }
            const title = payload?.data?.title || "New Notification"
            const body = payload?.data?.body || ""
            themedToast({ title, body })
            queryClient.invalidateQueries(["getNotifications"])
        })

        return unsubscribe
    }, [queryClient])

    // iOS WebKit: Firebase SDK's onMessage doesn't fire. The SW uses
    // clients.matchAll() to detect the app is open, then delivers via
    // client.postMessage() which is more reliable than BroadcastChannel
    // for SW→page communication when the page resumes from suspension.
    useEffect(() => {
        function handleSWMessage(event) {
            if (event.data?.type !== "FOREGROUND_PUSH") return
            themedToast({
                title: event.data?.data?.title || "New Notification",
                body: event.data?.data?.body || "",
            })
            queryClient.invalidateQueries(["getNotifications"])
        }
        navigator.serviceWorker?.addEventListener("message", handleSWMessage)
        return () =>
            navigator.serviceWorker?.removeEventListener(
                "message",
                handleSWMessage
            )
    }, [queryClient])
}
